import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Play, Star, Plus, Check, Clock } from "lucide-react";
import { isInMyList, toggleMyList } from "../lib/localStore";
import { mediaUrl } from "../lib/media";

export default function SeriesDetail() {
    const { id } = useParams();
    const [s, setS] = useState(null);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        api.get(`/series/${id}`).then((r) => {
            setS(r.data);
            setSaved(isInMyList(r.data.id, "series"));
        });
    }, [id]);

    if (!s) {
        return <div className="pt-28 mx-auto max-w-[1600px] px-6 text-white/60">Loading…</div>;
    }

    const released = (s.episodes || []).filter(e => !e.is_coming_soon);
    const upcoming = (s.episodes || []).filter(e => e.is_coming_soon);
    const firstEp = released[0];

    return (
        <div data-testid="series-detail-page">
            {/* Hero */}
            <section className="relative min-h-[75vh] w-full overflow-hidden">
                <img src={mediaUrl(s.backdrop)} alt={s.title} className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0B] via-[#0B0B0B]/70 to-[#0B0B0B]/30" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#0B0B0B] via-transparent to-[#0B0B0B]/40" />
                <div className="relative z-10 pt-40 pb-20 mx-auto max-w-[1600px] px-6 lg:px-10 flex flex-col justify-end min-h-[75vh]">
                    <div className="max-w-2xl">
                        <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#FFD400]">Apni Dukan Original</div>
                        <h1 className="mt-3 font-display font-black text-6xl lg:text-7xl tracking-tight text-white title-glow">{s.title}</h1>
                        <div className="mt-4 flex items-center gap-4 text-sm text-white/80">
                            <span>{s.genre}</span>
                            <span className="w-1 h-1 rounded-full bg-white/40" />
                            <span className="flex items-center gap-1"><Star className="w-4 h-4 fill-[#FFD400] text-[#FFD400]" />{s.rating?.toFixed(1)}</span>
                            <span className="w-1 h-1 rounded-full bg-white/40" />
                            <span>{released.length} Episodes</span>
                        </div>
                        <p className="mt-4 text-white/75 leading-relaxed">{s.description}</p>
                        <div className="mt-6 flex items-center gap-3 flex-wrap">
                            {firstEp && (
                                <Link
                                    to={`/watch/episode/${s.id}/${firstEp.id}`}
                                    data-testid="detail-play-btn"
                                    className="inline-flex items-center gap-2 bg-[#FFD400] text-black font-bold rounded-full px-7 py-3.5 hover:scale-105 transition-transform"
                                >
                                    <Play className="w-5 h-5 fill-black" /> Play Part {firstEp.part_number}
                                </Link>
                            )}
                            <button
                                data-testid="detail-mylist-btn"
                                onClick={() => { toggleMyList({ id: s.id, type: "series", title: s.title, thumbnail: s.poster }); setSaved(isInMyList(s.id, "series")); }}
                                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md text-white rounded-full px-7 py-3.5 border border-white/20 hover:bg-white/20 transition-colors"
                            >
                                {saved ? <Check className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
                                {saved ? "In My List" : "My List"}
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Episodes */}
            <section className="mx-auto max-w-[1600px] px-6 lg:px-10 mt-8 pb-24">
                <h2 className="font-display font-bold text-3xl mb-6">Episodes</h2>
                <div className="grid grid-cols-1 gap-4">
                    {(s.episodes || []).map((ep) => (
                        <div
                            key={ep.id}
                            data-testid={`episode-row-${ep.part_number}`}
                            className="group flex flex-col sm:flex-row gap-4 p-4 rounded-[18px] border border-white/5 bg-white/[0.02] hover:bg-white/[0.04] transition-colors"
                        >
                            <div className="relative sm:w-64 aspect-video rounded-[14px] overflow-hidden shrink-0 bg-white/5">
                                <img src={mediaUrl(ep.thumbnail || s.backdrop)} alt={ep.title} className="w-full h-full object-cover" />
                                <div className="absolute inset-0 bg-black/30" />
                                {ep.is_coming_soon && (
                                    <span className="absolute top-2 left-2 text-[10px] font-bold uppercase tracking-wider bg-[#FFD400] text-black px-2 py-1 rounded">
                                        Coming Soon
                                    </span>
                                )}
                                {!ep.is_coming_soon && (
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <span className="w-12 h-12 rounded-full bg-black/60 backdrop-blur-md border border-white/20 flex items-center justify-center group-hover:bg-[#FFD400] transition-colors">
                                            <Play className="w-5 h-5 fill-white text-white group-hover:fill-black group-hover:text-black transition-colors" />
                                        </span>
                                    </div>
                                )}
                            </div>
                            <div className="flex-1 flex flex-col justify-center">
                                <div className="flex items-baseline gap-3">
                                    <span className="text-[11px] uppercase tracking-widest text-[#FFD400] font-bold">Part {ep.part_number}</span>
                                    {ep.released_at && <span className="text-xs text-white/50">{ep.released_at}</span>}
                                </div>
                                <h3 className="mt-1 font-display font-bold text-xl text-white">{ep.title}</h3>
                                <p className="mt-1 text-sm text-white/60 max-w-2xl line-clamp-2">{ep.description}</p>
                                <div className="mt-2 flex items-center gap-2 text-xs text-white/50">
                                    <Clock className="w-3 h-3" /> {ep.duration}
                                </div>
                            </div>
                            <div className="flex items-center">
                                {ep.is_coming_soon ? (
                                    <span className="text-xs uppercase font-bold tracking-widest text-white/40">Soon</span>
                                ) : (
                                    <Link
                                        to={`/watch/episode/${s.id}/${ep.id}`}
                                        data-testid={`play-ep-${ep.part_number}`}
                                        className="inline-flex items-center gap-2 bg-[#FFD400] text-black font-bold rounded-full px-5 py-2.5 hover:scale-105 transition-transform"
                                    >
                                        <Play className="w-4 h-4 fill-black" /> Play
                                    </Link>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
                {upcoming.length === 0 && released.length > 0 && (
                    <p className="text-white/50 text-sm mt-6">All released episodes streaming. More coming soon.</p>
                )}
            </section>
        </div>
    );
}
